import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Plus,
  AlertTriangle,
  Search,
  Lock,
  Filter,
  Briefcase,
  Building2,
  Repeat,
  CheckCircle2,
  Hourglass,
  ListChecks,
  Route,
  FolderTree,
  CalendarDays,
  MapPin,
  UserRound,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { jobsApi } from '@/services/jobs'
import { workersApi } from '@/services/workers'
import jobWorkflowApi from '@/services/job-workflow'
import { assignmentsApi } from '@/services/assignments'
import { categoriesApi } from '@/services/categories'
import type { Job, Worker, WorkDayEntry } from '@/types'
import { getErrorMessage } from '@/lib/api'
import { resolveViewModeFromLocation } from '@/lib/list-view-mode'
import {
  buildJobsListPath,
  JOBS_LIST_DEFAULT_ORDERING,
  JOBS_LIST_PATHNAME,
  PROJECTS_LIST_PATHNAME,
  EVENTS_LIST_PATHNAME,
  JOBS_LIST_VIEW_DEFAULT,
  JOBS_LIST_VIEW_STORAGE_KEY,
  navigateToJobsListReturnPath,
  parseCalendarDateFromParam,
  parseJobsListSearch,
  storeJobsListReturnPath,
} from '@/lib/jobs-list-navigation'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getLocationReturnPath, withReturnTo } from '@/lib/return-navigation'
import { cn, formatCurrency } from '@/lib/utils'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { ListCardsCalendarViewTabs } from '@/components/common/list-cards-calendar-view-tabs'
import { CompactTableDate, commerceTableClassName } from '@/components/common/compact-table-date'
import { OrderDetailsDialog } from '@/components/orders/order-details-dialog'
import { JobChatSheet } from '@/components/chat/job-chat-sheet'
import { useChatFeatures } from '@/hooks/use-chat-features'
import { UnassignedAssignmentsAttention } from '@/components/jobs/unassigned-assignments-attention'
import { WorkDayEntriesAttention } from '@/components/jobs/work-day-entries-attention'
import { JobListActionsMenu } from '@/components/jobs/job-list-actions-menu'
import { JobListCard, JobListCardSkeleton } from '@/components/jobs/job-list-card'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { JobOverdueBadge } from '@/components/jobs/job-overdue-badge'
import { JobsCalendarView } from '@/components/jobs/jobs-calendar-view'
import {
  formatJobAmount,
  formatJobPaymentColumn,
  getJobBudgetOverrun,
  getJobCategoryColor,
  getJobSpendSummary,
  getRecentJobLabel,
  getRecurrenceLabel,
  isNewJob,
  JOB_LOCKED_BADGE_CLASS,
  JOB_SCHEDULE_FILTER_OPTIONS,
  JOB_SORT_OPTIONS,
  jobPriorityColors,
  jobPriorityLabels,
  jobScheduleStateBadgeClasses,
  jobScheduleStateLabels,
  jobStatusColors,
  resolveJobCategoryKey,
  resolveJobScheduleState,
  toJobCalendarDateKey,
} from '@/lib/job-list-display'
import {
  getCalendarMonthGridBounds,
  mergeJobsById,
} from '@/lib/job-recurrence-calendar'

const getWorkerFilterLabel = (
  worker: Pick<Worker, 'full_name' | 'first_name' | 'last_name' | 'worker_code'> | undefined,
  fallbackId: string,
) => {
  const fullName = String(worker?.full_name ?? '').trim()
  const firstName = String(worker?.first_name ?? '').trim()
  const lastName = String(worker?.last_name ?? '').trim()
  const joinedName = [firstName, lastName].filter(Boolean).join(' ').trim()
  const code = String(worker?.worker_code ?? '').trim()
  return fullName || joinedName || code || (fallbackId ? `Worker ${fallbackId}` : 'Worker')
}

const getWorkerFilterValue = (worker: Worker) => String(worker.worker_id ?? worker.id ?? '')

type JobsListViewMode = 'list' | 'cards' | 'calendar'

const STATUS_FILTER_OPTIONS = [
  { value: 'draft', label: 'Draft', badgeClassName: 'border-slate-200 bg-slate-50 text-slate-700', activeClassName: 'border-slate-400 bg-slate-100 ring-1 ring-slate-300' },
  { value: 'open', label: 'Open', badgeClassName: 'border-blue-200 bg-blue-50 text-blue-700', activeClassName: 'border-blue-400 bg-blue-100 ring-1 ring-blue-300' },
  { value: 'in_progress', label: 'In Progress', badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700', activeClassName: 'border-amber-400 bg-amber-100 ring-1 ring-amber-300' },
  { value: 'completed', label: 'Completed', badgeClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700', activeClassName: 'border-emerald-400 bg-emerald-100 ring-1 ring-emerald-300' },
  { value: 'on_hold', label: 'On Hold', badgeClassName: 'border-orange-200 bg-orange-50 text-orange-700', activeClassName: 'border-orange-400 bg-orange-100 ring-1 ring-orange-300' },
  { value: 'cancelled', label: 'Cancelled', badgeClassName: 'border-rose-200 bg-rose-50 text-rose-700', activeClassName: 'border-rose-400 bg-rose-100 ring-1 ring-rose-300' },
] as const

const PRIORITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All priority' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'critical', label: 'Critical' },
] as const

const RECURRENCE_FILTER_OPTIONS = [
  { value: 'all', label: 'All recurrence' },
  { value: 'recurring', label: 'Recurring only' },
  { value: 'single', label: 'One-off' },
  { value: 'source', label: 'Series sources' },
  { value: 'generated', label: 'Generated occurrences' },
] as const

const VISIBLE_CATEGORY_FILTER_COUNT = 5

const toLocalDateKey = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatQuickDateLabel = (date: Date) =>
  date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })

type JobsListPageProps = {
  portalMode?: 'job' | 'project' | 'event'
}

export default function JobsListPage({ portalMode: _portalMode = 'job' }: JobsListPageProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const labels = useWorkspaceLabels()
  const isEventsPortal = labels.mode === 'event'
  const isProjectsPortal = labels.mode === 'project'
  const portalBasePath = labels.basePath === '/events' ? EVENTS_LIST_PATHNAME : labels.basePath === '/projects' ? PROJECTS_LIST_PATHNAME : JOBS_LIST_PATHNAME
  const listSearch = useMemo(() => parseJobsListSearch(location.search), [location.search])
  const resolvedViewMode = useMemo(
    () => resolveViewModeFromLocation(
      location.search,
      JOBS_LIST_VIEW_STORAGE_KEY,
      ['list', 'cards', 'calendar'] as const,
      JOBS_LIST_VIEW_DEFAULT,
    ),
    [location.search],
  )
  const serviceInstanceId = listSearch.serviceInstanceId ?? getServiceInstanceIdFromSearch(location.search)
  const currentReturnPath = getLocationReturnPath(location)
  const { data: chatFeatures } = useChatFeatures(Boolean(serviceInstanceId))
  const chatEnabled = chatFeatures?.enabled !== false
  const {
    page,
    pageSize,
    orderId: orderFilter,
    statusFilter,
    priorityFilter,
    recurrenceFilter,
    categoryFilter,
    scheduleFilter,
    sortOrder,
    clientUser: clientUserFilter,
    jobLocationId: jobLocationFilter,
    primaryWorkerId: primaryWorkerFilter,
    jobScope: jobScopeFilter,
    projectId: projectIdFilter,
    dateFilter,
    startDate,
    endDate,
    calendarDate,
    search: urlSearch,
  } = listSearch

  const [search, setSearch] = useState(urlSearch)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [selectedOrderReferenceId, setSelectedOrderReferenceId] = useState<string | null>(null)
  const [chatJob, setChatJob] = useState<Job | null>(null)
  const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now())
  const [viewMode, setViewMode] = useState<JobsListViewMode>(resolvedViewMode)
  const [showAllCategoryFilters, setShowAllCategoryFilters] = useState(false)
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(
    () => parseCalendarDateFromParam(calendarDate) ?? new Date(),
  )
  const [visibleCalendarMonth, setVisibleCalendarMonth] = useState(
    () => {
      const initial = parseCalendarDateFromParam(calendarDate) ?? new Date()
      return new Date(initial.getFullYear(), initial.getMonth(), 1)
    },
  )
  const deferredSearch = useDeferredValue(search.trim())
  const deleteCancellationReasonsQuery = useQuery({
    queryKey: ['job-cancellation-reasons', 'job', deleteId],
    queryFn: () => jobWorkflowApi.runtime.getCancellationReasons('job', { entity_id: deleteId! }),
    enabled: Boolean(deleteId),
    staleTime: 30_000,
  })

  useEffect(() => {
    setViewMode(resolvedViewMode)
  }, [resolvedViewMode])

  useEffect(() => {
    const parsed = parseCalendarDateFromParam(calendarDate)
    if (parsed) {
      setSelectedCalendarDate(parsed)
      setVisibleCalendarMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1))
    }
  }, [calendarDate])


  const replaceListQuery = (updates: Partial<{
    search: string | null
    orderId: string | null
    statusFilter: string | null
    priorityFilter: string | null
    recurrenceFilter: string | null
    categoryFilter: string | null
    scheduleFilter: string | null
    sortOrder: string | null
    clientUser: string | number | null
    jobLocationId: string | number | null
    primaryWorkerId: string | number | null
    jobScope: 'open' | 'completed' | null
    projectId: string | number | null
    isProject: 'true' | 'false' | null
    topLevel: 'true' | 'false' | null
    dateFilter: 'today' | 'yesterday' | 'this_month' | 'last_month' | 'custom' | null
    startDate: string | null
    endDate: string | null
    viewMode?: JobsListViewMode | null
    calendarDate?: string | null
    page: number
    pageSize: number
  }>) => {
    const nextPath = buildJobsListPath({
      basePath: portalBasePath,
      serviceInstanceId,
      search: updates.search !== undefined ? (updates.search ?? '') : search,
      orderId: updates.orderId !== undefined ? (updates.orderId ?? '') : orderFilter,
      statusFilter: updates.statusFilter !== undefined ? (updates.statusFilter ?? 'all') : statusFilter,
      priorityFilter: updates.priorityFilter !== undefined ? (updates.priorityFilter ?? 'all') : priorityFilter,
      recurrenceFilter: updates.recurrenceFilter !== undefined ? (updates.recurrenceFilter ?? 'all') : recurrenceFilter,
      categoryFilter: updates.categoryFilter !== undefined ? (updates.categoryFilter ?? 'all') : categoryFilter,
      scheduleFilter: updates.scheduleFilter !== undefined ? (updates.scheduleFilter ?? 'all') : scheduleFilter,
      sortOrder: updates.sortOrder !== undefined ? (updates.sortOrder ?? JOBS_LIST_DEFAULT_ORDERING) : sortOrder,
      clientUser: updates.clientUser !== undefined ? updates.clientUser : (clientUserFilter || null),
      jobLocationId: updates.jobLocationId !== undefined ? updates.jobLocationId : (jobLocationFilter || null),
      primaryWorkerId: updates.primaryWorkerId !== undefined ? updates.primaryWorkerId : (primaryWorkerFilter || null),
      jobScope: updates.jobScope !== undefined ? updates.jobScope : (jobScopeFilter === 'open' || jobScopeFilter === 'completed' ? jobScopeFilter : null),
      projectId: updates.projectId !== undefined ? updates.projectId : (projectIdFilter || null),
      isProject: isProjectScope
        ? 'true'
        : null,
      topLevel: isProjectScope
        ? 'true'
        : null,
      dateFilter: updates.dateFilter !== undefined ? updates.dateFilter : (dateFilter === 'custom' || dateFilter === 'today' || dateFilter === 'yesterday' || dateFilter === 'this_month' || dateFilter === 'last_month' ? dateFilter : null),
      startDate: updates.startDate !== undefined ? updates.startDate : (startDate || null),
      endDate: updates.endDate !== undefined ? updates.endDate : (endDate || null),
      viewMode: updates.viewMode !== undefined ? updates.viewMode : viewMode,
      calendarDate: updates.calendarDate !== undefined ? updates.calendarDate : calendarDate,
      page: updates.page ?? page,
      pageSize: updates.pageSize ?? pageSize,
    })

    navigateToJobsListReturnPath(navigate, nextPath, { replace: true })
  }

  const handleViewModeChange = (nextMode: JobsListViewMode) => {
    setViewMode(nextMode)
    replaceListQuery({ page: 1, viewMode: nextMode })
    try {
      localStorage.setItem(JOBS_LIST_VIEW_STORAGE_KEY, nextMode)
    } catch {
      // Ignore storage failures.
    }
  }

  useEffect(() => {
    if (statusFilter === 'open' && scheduleFilter === 'upcoming' && dateFilter === 'custom' && startDate && endDate) {
      replaceListQuery({ scheduleFilter: 'all', page: 1 })
    }
  }, [dateFilter, endDate, scheduleFilter, startDate, statusFilter])

  useEffect(() => {
    if (viewMode !== 'calendar' && calendarDate && !dateFilter && !startDate && !endDate) {
      replaceListQuery({
        dateFilter: 'custom',
        startDate: calendarDate,
        endDate: calendarDate,
        page: 1,
      })
    }
  }, [calendarDate, dateFilter, endDate, startDate, viewMode])

  const toggleStatusFilter = (nextStatus: string) => {
    replaceListQuery({
      statusFilter: statusFilter === nextStatus ? 'all' : nextStatus,
      page: 1,
    })
  }

  const toggleCategoryFilter = (nextCategoryId: string) => {
    replaceListQuery({
      categoryFilter: categoryFilter === nextCategoryId ? 'all' : nextCategoryId,
      page: 1,
    })
  }

  const toggleScheduleFilter = (nextSchedule: string) => {
    replaceListQuery({
      scheduleFilter: scheduleFilter === nextSchedule ? 'all' : nextSchedule,
      page: 1,
    })
  }

  const hasActiveFilters = Boolean(
    search.trim()
    || orderFilter
    || statusFilter !== 'all'
    || priorityFilter !== 'all'
    || recurrenceFilter !== 'all'
    || categoryFilter !== 'all'
    || scheduleFilter !== 'all'
    || sortOrder !== JOBS_LIST_DEFAULT_ORDERING
    || clientUserFilter
    || jobLocationFilter
    || primaryWorkerFilter
    || jobScopeFilter
    || projectIdFilter
    || dateFilter
    || startDate
    || endDate
  )

  const normalizedJobScope = jobScopeFilter === 'open' || jobScopeFilter === 'completed'
    ? jobScopeFilter
    : undefined
  const isEventScope = isEventsPortal
  const isProjectScope = isProjectsPortal
  const entityBasePath = labels.basePath
  const entityLabel = labels.entity
  const entityLabelPlural = labels.entityPlural
  const deleteConfirmationPhrase = labels.deleteConfirmationPhrase
  const fetchJobsPage = isEventScope ? jobsApi.getEvents : isProjectScope ? jobsApi.getProjects : jobsApi.getJobs
  const recurrenceFilterOptions = RECURRENCE_FILTER_OPTIONS.map((option) => (
    option.value === 'single' ? { ...option, label: `One-off ${labels.entityPluralLower}` } : option
  ))

  const navigateToJobDetail = (jobId: string) => {
    const returnPath = buildJobsListPath({
      basePath: portalBasePath,
      serviceInstanceId,
      search,
      orderId: orderFilter,
      statusFilter,
      priorityFilter,
      recurrenceFilter,
      categoryFilter,
      scheduleFilter,
      sortOrder,
      page,
      pageSize,
      clientUser: clientUserFilter || null,
      jobLocationId: jobLocationFilter || null,
      primaryWorkerId: primaryWorkerFilter || null,
      jobScope: normalizedJobScope ?? null,
      projectId: projectIdFilter || null,
      isProject: isProjectScope ? 'true' : null,
      topLevel: isProjectScope ? 'true' : null,
      dateFilter: dateFilter === 'custom' || dateFilter === 'today' || dateFilter === 'yesterday' || dateFilter === 'this_month' || dateFilter === 'last_month' ? dateFilter : null,
      startDate: startDate || null,
      endDate: endDate || null,
      viewMode,
    })

    storeJobsListReturnPath(returnPath)
    navigate(withServiceInstanceId(`${entityBasePath}/${jobId}`, serviceInstanceId), {
      state: { returnTo: returnPath },
    })
  }

  const openJobChat = (job: Job) => {
    setChatJob(job)
  }

  useEffect(() => {
    setSearch(urlSearch)
  }, [urlSearch])

  useEffect(() => {
    if (
      location.pathname === JOBS_LIST_PATHNAME
      || location.pathname === PROJECTS_LIST_PATHNAME
      || location.pathname === EVENTS_LIST_PATHNAME
    ) {
      storeJobsListReturnPath(`${location.pathname}${location.search}`)
    }
  }, [location.pathname, location.search])

  useEffect(() => {
    if (deferredSearch === urlSearch.trim()) {
      return
    }

    const nextPath = buildJobsListPath({
      basePath: portalBasePath,
      serviceInstanceId,
      search: deferredSearch,
      orderId: orderFilter,
      statusFilter,
      priorityFilter,
      recurrenceFilter,
      categoryFilter,
      scheduleFilter,
      sortOrder,
      clientUser: clientUserFilter || null,
      jobLocationId: jobLocationFilter || null,
      primaryWorkerId: primaryWorkerFilter || null,
      jobScope: normalizedJobScope ?? null,
      projectId: projectIdFilter || null,
      isProject: isProjectScope ? 'true' : null,
      topLevel: isProjectScope ? 'true' : null,
      dateFilter: dateFilter === 'custom' || dateFilter === 'today' || dateFilter === 'yesterday' || dateFilter === 'this_month' || dateFilter === 'last_month' ? dateFilter : null,
      startDate: startDate || null,
      endDate: endDate || null,
      viewMode,
      page: 1,
      pageSize,
    })

    navigateToJobsListReturnPath(navigate, nextPath, { replace: true })
  }, [deferredSearch, urlSearch, portalBasePath, serviceInstanceId, orderFilter, statusFilter, priorityFilter, recurrenceFilter, categoryFilter, scheduleFilter, sortOrder, clientUserFilter, jobLocationFilter, primaryWorkerFilter, normalizedJobScope, projectIdFilter, isProjectScope, dateFilter, startDate, endDate, pageSize, navigate])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRelativeTimeNow(Date.now())
    }, 60 * 1000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [])

  const normalizedStatus = statusFilter === 'all' ? undefined : statusFilter
  const normalizedPriority = priorityFilter === 'all' ? undefined : priorityFilter
  const normalizedRecurrence = recurrenceFilter === 'all' ? undefined : recurrenceFilter as 'recurring' | 'single' | 'generated' | 'source'
  const normalizedCategory = categoryFilter === 'all' ? undefined : categoryFilter
  const normalizedSchedule = scheduleFilter === 'all'
    ? undefined
    : scheduleFilter as 'upcoming' | 'overdue' | 'in_progress'

  const categoriesQuery = useQuery({
    queryKey: ['job-list-categories', isProjectScope ? 'project' : 'job'],
    queryFn: () => categoriesApi.getCategories({ kind: isProjectScope ? 'project' : 'job' }),
    staleTime: 120_000,
  })

  const workerFilterQuery = useQuery({
    queryKey: ['job-list-worker-filter', primaryWorkerFilter],
    queryFn: () => workersApi.getWorker(primaryWorkerFilter),
    enabled: Boolean(primaryWorkerFilter),
    staleTime: 120_000,
  })

  const workerFilterOptionsQuery = useQuery({
    queryKey: ['job-list-worker-filter-options', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ active: true, page_size: 1000, service_instance_id: serviceInstanceId || undefined }),
    staleTime: 120_000,
  })

  const workerFilterOptions = useMemo(() => {
    const options = [...(workerFilterOptionsQuery.data?.results ?? [])]
    const selectedWorker = workerFilterQuery.data
    if (selectedWorker) {
      const selectedValue = getWorkerFilterValue(selectedWorker)
      if (selectedValue && !options.some((worker) => getWorkerFilterValue(worker) === selectedValue)) {
        options.unshift(selectedWorker)
      }
    }
    return options
  }, [workerFilterOptionsQuery.data?.results, workerFilterQuery.data])

  const quickDateFilters = useMemo(() => {
    const today = new Date()
    return Array.from({ length: 4 }, (_, index) => {
      const date = new Date(today)
      date.setDate(today.getDate() + index)
      const dateKey = toLocalDateKey(date)
      return {
        key: dateKey,
        label: index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : formatQuickDateLabel(date),
        dateKey,
      }
    })
  }, [])

  const applyQuickDateFilter = (dateKey: string) => {
    replaceListQuery({
      dateFilter: 'custom',
      startDate: dateKey,
      endDate: dateKey,
      calendarDate: dateKey,
      page: 1,
    })
  }

  const calendarGridBounds = useMemo(
    () => getCalendarMonthGridBounds(visibleCalendarMonth),
    [visibleCalendarMonth],
  )

  const sharedJobQueryFilters = {
    search: deferredSearch || undefined,
    order_id: orderFilter || undefined,
    status: normalizedStatus,
    priority: normalizedPriority,
    category: normalizedCategory,
    schedule_state: normalizedSchedule,
    client_user: clientUserFilter || undefined,
    job_location_id: jobLocationFilter || undefined,
    worker: primaryWorkerFilter || undefined,
    job_scope: normalizedJobScope as 'open' | 'completed' | undefined,
    project_id: projectIdFilter || undefined,
    is_project: isEventScope
      ? undefined
      : isProjectScope
        ? true
        : undefined,
    top_level: isEventScope
      ? undefined
      : isProjectScope
        ? true
        : undefined,
  }

  const listJobsQuery = useQuery({
    enabled: viewMode !== 'calendar',
    queryKey: [
      'jobs',
      page,
      pageSize,
      deferredSearch,
      orderFilter,
      normalizedStatus,
      normalizedPriority,
      normalizedRecurrence,
      normalizedCategory,
      normalizedSchedule,
      sortOrder,
      clientUserFilter,
      jobLocationFilter,
      primaryWorkerFilter,
      normalizedJobScope,
      projectIdFilter,
      isProjectScope,
      dateFilter,
      startDate,
      endDate,
      'list',
    ],
    queryFn: () =>
      (fetchJobsPage)({
        page,
        page_size: pageSize,
        ...sharedJobQueryFilters,
        recurrence_state: normalizedRecurrence,
        ordering: sortOrder || JOBS_LIST_DEFAULT_ORDERING,
        date_filter: dateFilter === 'custom' || dateFilter === 'today' || dateFilter === 'yesterday' || dateFilter === 'this_month' || dateFilter === 'last_month' ? dateFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      }),
  })

  const calendarJobQueries = useQueries({
    queries: [
      {
        enabled: viewMode === 'calendar',
        queryKey: [
          'jobs',
          'calendar-range',
          toJobCalendarDateKey(calendarGridBounds.gridStart),
          toJobCalendarDateKey(calendarGridBounds.gridEnd),
          deferredSearch,
          orderFilter,
          normalizedStatus,
          normalizedPriority,
          normalizedRecurrence,
          normalizedCategory,
          normalizedSchedule,
          clientUserFilter,
          jobLocationFilter,
          primaryWorkerFilter,
          normalizedJobScope,
          projectIdFilter,
          isProjectScope,
        ],
        queryFn: () =>
          (fetchJobsPage)({
            page: 1,
            page_size: 500,
            ...sharedJobQueryFilters,
            recurrence_state: normalizedRecurrence,
            ordering: 'scheduled_start',
            date_filter: 'custom',
            start_date: toJobCalendarDateKey(calendarGridBounds.gridStart),
            end_date: toJobCalendarDateKey(calendarGridBounds.gridEnd),
          }),
        placeholderData: (previous: Awaited<ReturnType<typeof jobsApi.getJobs>> | undefined) => previous,
        staleTime: 30_000,
      },
      {
        enabled: viewMode === 'calendar',
        queryKey: [
          'jobs',
          'calendar-recurring',
          deferredSearch,
          orderFilter,
          normalizedStatus,
          normalizedPriority,
          normalizedCategory,
          normalizedSchedule,
          clientUserFilter,
          jobLocationFilter,
          primaryWorkerFilter,
          normalizedJobScope,
          projectIdFilter,
          isProjectScope,
        ],
        queryFn: () =>
          (fetchJobsPage)({
            page: 1,
            page_size: 300,
            ...sharedJobQueryFilters,
            recurrence_state: 'recurring',
            ordering: 'scheduled_start',
          }),
        placeholderData: (previous: Awaited<ReturnType<typeof jobsApi.getJobs>> | undefined) => previous,
        staleTime: 30_000,
      },
    ],
  })

  const data = viewMode === 'calendar'
    ? {
        count: mergeJobsById(
          calendarJobQueries[0]?.data?.results ?? [],
          calendarJobQueries[1]?.data?.results ?? [],
        ).length,
        results: mergeJobsById(
          calendarJobQueries[0]?.data?.results ?? [],
          calendarJobQueries[1]?.data?.results ?? [],
        ),
      }
    : listJobsQuery.data

  const isLoading = viewMode === 'calendar'
    ? calendarJobQueries.some((query) => query.isLoading)
    : listJobsQuery.isLoading

  const calendarWorkDayEntriesQuery = useQuery({
    enabled: viewMode === 'calendar',
    queryKey: [
      'workday-entries',
      'calendar-range',
      toJobCalendarDateKey(calendarGridBounds.gridStart),
      toJobCalendarDateKey(calendarGridBounds.gridEnd),
      deferredSearch,
      normalizedStatus,
      primaryWorkerFilter,
      normalizedJobScope,
    ],
    queryFn: () =>
      assignmentsApi.getWorkDayEntries({
        page: 1,
        page_size: 1000,
        search: deferredSearch || undefined,
        worker_id: primaryWorkerFilter || undefined,
        status: normalizedStatus,
        date_from: toJobCalendarDateKey(calendarGridBounds.gridStart),
        date_to: toJobCalendarDateKey(calendarGridBounds.gridEnd),
      }),
    placeholderData: (previous: Awaited<ReturnType<typeof assignmentsApi.getWorkDayEntries>> | undefined) => previous,
    staleTime: 30_000,
  })

  const calendarWorkDayEntries = useMemo<WorkDayEntry[]>(() => {
    if (viewMode !== 'calendar') {
      return []
    }
    return calendarWorkDayEntriesQuery.data?.results ?? []
  }, [viewMode, calendarWorkDayEntriesQuery.data?.results])

  const calendarViewLoading = viewMode === 'calendar'
    ? isLoading || calendarWorkDayEntriesQuery.isLoading
    : isLoading

  const error = viewMode === 'calendar'
    ? calendarJobQueries.find((query) => query.error)?.error
    : listJobsQuery.error

  const { data: unassignedAssignmentsSummary, isLoading: unassignedAssignmentsLoading } = useQuery({
    queryKey: ['unassigned-assignments-count', serviceInstanceId],
    queryFn: () => assignmentsApi.getAssignments({ unassigned: true, page_size: 1 }),
    staleTime: 30_000,
  })

  const { data: attentionWorkDayEntriesSummary, isLoading: attentionWorkDayEntriesLoading } = useQuery({
    queryKey: ['attention-work-day-entries-count', serviceInstanceId],
    queryFn: () => assignmentsApi.getWorkDayEntries({ needs_supervisor_attention: true, page_size: 1 }),
    staleTime: 30_000,
  })

  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => jobsApi.delete(id, { reason }),
    onSuccess: () => {
      toast.success(`${entityLabel} deleted successfully`)
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      setDeleteId(null)
      setDeleteConfirmInput('')
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const lockMutation = useMutation({
    mutationFn: ({ id, lock }: { id: string; lock: boolean }) =>
      lock ? jobsApi.lock(id) : jobsApi.unlock(id),
    onSuccess: (_, variables) => {
      toast.success(`${entityLabel} ${variables.lock ? 'locked' : 'unlocked'} successfully`)
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const jobs = data?.results ?? []
  const backendStatusCounts = listJobsQuery.data?.statistics_data?.status_counts
  const categories = categoriesQuery.data?.results ?? []
  const visibleCategories = showAllCategoryFilters
    ? categories
    : categories.slice(0, VISIBLE_CATEGORY_FILTER_COUNT)
  const hiddenCategoryCount = Math.max(0, categories.length - visibleCategories.length)

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const job of jobs) {
      const key = resolveJobCategoryKey(job)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return counts
  }, [jobs])

  const scheduleCounts = useMemo(() => ({
    upcoming: jobs.filter((job) => resolveJobScheduleState(job, relativeTimeNow) === 'upcoming').length,
    overdue: jobs.filter((job) => resolveJobScheduleState(job, relativeTimeNow) === 'overdue').length,
    in_progress: jobs.filter((job) => resolveJobScheduleState(job, relativeTimeNow) === 'in_progress').length,
  }), [jobs, relativeTimeNow])
  
  // Status breakdown counts — use backend-provided totals (across all pages) when
  // available; fall back to counting the current page only while the query is loading.
  const statusCounts = useMemo(() => {
    if (backendStatusCounts) {
      const toCount = (key: string) => Number(backendStatusCounts[key] ?? 0)
      return {
        draft: toCount('draft'),
        open: toCount('open'),
        in_progress: toCount('in_progress'),
        completed: toCount('completed'),
        cancelled: toCount('cancelled'),
        on_hold: toCount('on_hold'),
      }
    }
    return {
      draft: jobs.filter((job) => job.status === 'draft').length,
      open: jobs.filter((job) => job.status === 'open').length,
      in_progress: jobs.filter((job) => job.status === 'in_progress').length,
      completed: jobs.filter((job) => job.status === 'completed').length,
      cancelled: jobs.filter((job) => job.status === 'cancelled').length,
      on_hold: jobs.filter((job) => job.status === 'on_hold').length,
    }
  }, [backendStatusCounts, jobs])
  
  // Live jobs: currently in progress
  const liveJobs = statusCounts.in_progress
  
  // Scheduled jobs: open status or future scheduled_start
  const scheduledJobs = jobs.filter((job) => {
    if (job.status === 'open') return true
    if (job.scheduled_start) {
      const startDate = new Date(job.scheduled_start)
      return startDate > new Date()
    }
    return false
  }).length

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-destructive/20 bg-card">
        <div className="text-center">
          <p className="mb-2 text-destructive">{`Error loading ${entityLabelPlural.toLowerCase()}`}</p>
          <p className="mb-4 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-2">
      <section className="rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <Badge variant="outline" className="w-fit rounded-md border-blue-200 bg-blue-50 text-blue-700">
              {labels.workspaceTitle}
            </Badge>
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
              <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-muted-foreground">
                <Briefcase className="mr-1 inline h-3.5 w-3.5" />
                {data?.count ?? 0} visible
              </span>
              <UnassignedAssignmentsAttention
                count={unassignedAssignmentsSummary?.count ?? 0}
                isLoading={unassignedAssignmentsLoading}
                serviceInstanceId={serviceInstanceId}
              />
              <WorkDayEntriesAttention
                count={attentionWorkDayEntriesSummary?.count ?? 0}
                isLoading={attentionWorkDayEntriesLoading}
                serviceInstanceId={serviceInstanceId}
                returnPath={currentReturnPath}
              />
              <span className="rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-green-700">
                <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                {liveJobs} live
              </span>
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700">
                <Hourglass className="mr-1 inline h-3.5 w-3.5" />
                {scheduledJobs} scheduled
              </span>
            </div>
          </div>

          <div className="flex flex-col items-stretch gap-3 lg:items-end">
            <div className="flex flex-col gap-1 lg:items-end">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                View
              </p>
              <ListCardsCalendarViewTabs value={viewMode} onChange={handleViewModeChange} className="lg:w-auto" />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button variant="outline" size="sm" className="rounded-lg" asChild>
                <Link to={withServiceInstanceId(`${entityBasePath}/lifecycle`, serviceInstanceId)}>
                  <Route className="mr-2 h-4 w-4" />
                  {`${entityLabel} lifecycle`}
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" asChild>
                <Link to={withServiceInstanceId(`/categories?kind=${isProjectScope ? 'project' : 'job'}`, serviceInstanceId)}>
                  <FolderTree className="mr-2 h-4 w-4" />
                  Manage categories
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" asChild>
                <Link to={withServiceInstanceId(`${entityBasePath}/checklists`, serviceInstanceId)}>
                  <ListChecks className="mr-2 h-4 w-4" />
                  Manage Checklist
                </Link>
              </Button>
              <Button size="sm" className="rounded-lg" asChild>
                <Link to={withServiceInstanceId(`${entityBasePath}/create`, serviceInstanceId)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {`Create ${entityLabel}`}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Card className="rounded-[28px] border-white/70 bg-card/90 shadow-[0_20px_40px_-35px_rgba(15,23,42,0.55)]">
        <CardHeader className="space-y-2 border-b border-border/60 pb-3">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="text-sm">Filters</CardTitle>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:flex-wrap">
              <div className="relative min-w-[240px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={`Search ${entityLabelPlural.toLowerCase()} by title, client, or location`}
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value)
                  }}
                  className="h-9 pl-9"
                />
              </div>
              <Select
                key={`status-${statusFilter}-${location.search}`}
                value={statusFilter}
                onValueChange={(value) => {
                  replaceListQuery({ statusFilter: value, page: 1 })
                }}
              >
                <SelectTrigger className="h-9 w-full sm:w-40">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  <SelectItem value="all">All status</SelectItem>
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                key={`priority-${priorityFilter}-${location.search}`}
                value={priorityFilter}
                onValueChange={(value) => {
                  replaceListQuery({ priorityFilter: value, page: 1 })
                }}
              >
                <SelectTrigger className="h-9 w-full sm:w-40">
                  <Building2 className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  {PRIORITY_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                key={`recurrence-${recurrenceFilter}-${location.search}`}
                value={recurrenceFilter}
                onValueChange={(value) => {
                  replaceListQuery({ recurrenceFilter: value, page: 1 })
                }}
              >
                <SelectTrigger className="h-9 w-full sm:w-48">
                  <Repeat className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Recurrence" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  {recurrenceFilterOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                key={`worker-${primaryWorkerFilter || 'all'}-${location.search}`}
                value={primaryWorkerFilter || 'all'}
                onValueChange={(value) => {
                  replaceListQuery({ primaryWorkerId: value === 'all' ? null : value, page: 1 })
                }}
              >
                <SelectTrigger className="h-9 w-full sm:w-48">
                  <UserRound className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Worker" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  <SelectItem value="all">All workers</SelectItem>
                  {workerFilterOptions.map((worker) => {
                    const value = getWorkerFilterValue(worker)
                    if (!value) return null
                    return (
                      <SelectItem key={value} value={value}>
                        {getWorkerFilterLabel(worker, value)}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
              <Select
                key={`sort-${sortOrder}-${location.search}`}
                value={sortOrder}
                onValueChange={(value) => {
                  replaceListQuery({ sortOrder: value, page: 1 })
                }}
              >
                <SelectTrigger className="h-9 w-full sm:w-44">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  {JOB_SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Quick date</span>
                {quickDateFilters.map((option) => {
                  const isActive = dateFilter === 'custom' && startDate === option.dateKey && endDate === option.dateKey
                  return (
                    <Button
                      key={option.key}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 rounded-full px-3 text-xs"
                      onClick={() => applyQuickDateFilter(option.dateKey)}
                    >
                      {option.label}
                    </Button>
                  )
                })}
              </div>
              {hasActiveFilters ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    setSearch('')
                    replaceListQuery({
                      search: '',
                      orderId: '',
                      statusFilter: 'all',
                      priorityFilter: 'all',
                      recurrenceFilter: 'all',
                      categoryFilter: 'all',
                      scheduleFilter: 'all',
                      clientUser: null,
                      jobLocationId: null,
                      primaryWorkerId: null,
                      jobScope: null,
                      sortOrder: JOBS_LIST_DEFAULT_ORDERING,
                      dateFilter: null,
                      startDate: null,
                      endDate: null,
                      calendarDate: null,
                      page: 1,
                    })
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
              {primaryWorkerFilter ? (
                <Badge variant="secondary" className="h-9 gap-1.5 rounded-lg border border-border/70 px-2.5">
                  <UserRound className="h-3.5 w-3.5" />
                  <span className="max-w-[180px] truncate">
                    Worker: {workerFilterQuery.isLoading ? 'Loading...' : getWorkerFilterLabel(workerFilterQuery.data, primaryWorkerFilter)}
                  </span>
                  <button
                    type="button"
                    className="ml-0.5 rounded p-0.5 hover:bg-background/80"
                    aria-label="Clear worker filter"
                    onClick={() => replaceListQuery({ primaryWorkerId: null, page: 1 })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </Badge>
              ) : null}
              {dateFilter || startDate || endDate ? (
                <Badge variant="secondary" className="h-9 gap-1.5 rounded-lg border border-border/70 px-2.5">
                  <CalendarDays className="h-3.5 w-3.5" />
                  <span className="max-w-[220px] truncate">
                    Date: {startDate && endDate ? `${startDate} to ${endDate}` : dateFilter || 'custom'}
                  </span>
                  <button
                    type="button"
                    className="ml-0.5 rounded p-0.5 hover:bg-background/80"
                    aria-label="Clear date filter"
                    onClick={() => replaceListQuery({ dateFilter: null, startDate: null, endDate: null, page: 1 })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-0.5 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => replaceListQuery({ statusFilter: 'all', page: 1 })}
                className={cn(
                  'rounded-md border px-2 py-0.5 text-[11px] font-medium transition hover:opacity-90',
                  statusFilter === 'all'
                    ? 'border-foreground/20 bg-foreground/5 text-foreground ring-1 ring-foreground/20'
                    : 'border-border/70 bg-muted/20 text-muted-foreground',
                )}
              >
                All
              </button>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleStatusFilter(option.value)}
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-[11px] font-medium transition hover:opacity-90',
                    statusFilter === option.value ? option.activeClassName : option.badgeClassName,
                  )}
                >
                  {option.label}: {statusCounts[option.value as keyof typeof statusCounts]}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 xl:justify-end">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Timing</span>
              {JOB_SCHEDULE_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    if (option.value === 'all') {
                      replaceListQuery({ scheduleFilter: 'all', page: 1 })
                      return
                    }
                    toggleScheduleFilter(option.value)
                  }}
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-[11px] font-medium transition hover:opacity-90',
                    scheduleFilter === option.value ? option.activeClassName : option.badgeClassName,
                  )}
                >
                  {option.label}
                  {option.value !== 'all'
                    ? `: ${scheduleCounts[option.value as keyof typeof scheduleCounts] ?? 0}`
                    : ''}
                </button>
              ))}
            </div>
          </div>
          {categories.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Category</span>
              <button
                type="button"
                onClick={() => replaceListQuery({ categoryFilter: 'all', page: 1 })}
                className={cn(
                  'rounded-md border px-2 py-0.5 text-[11px] font-medium transition hover:opacity-90',
                  categoryFilter === 'all'
                    ? 'border-foreground/20 bg-foreground/5 text-foreground ring-1 ring-foreground/20'
                    : 'border-border/70 bg-muted/20 text-muted-foreground',
                )}
              >
                All
              </button>
              {visibleCategories.map((category) => {
                const categoryId = String(category.id)
                const categoryColor = getJobCategoryColor(categoryId)
                const isActive = categoryFilter === categoryId

                return (
                  <button
                    key={categoryId}
                    type="button"
                    onClick={() => toggleCategoryFilter(categoryId)}
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-[11px] font-medium transition hover:opacity-90',
                      isActive
                        ? cn(categoryColor.badge, 'ring-1 ring-current/20')
                        : cn(categoryColor.badge, 'opacity-80'),
                    )}
                  >
                    {category.name || `Category ${category.id}`}
                    {`: ${categoryCounts.get(categoryId) ?? 0}`}
                  </button>
                )
              })}
              {categories.length > VISIBLE_CATEGORY_FILTER_COUNT ? (
                <button
                  type="button"
                  onClick={() => setShowAllCategoryFilters((current) => !current)}
                  className="rounded-md border border-border/70 bg-background px-2 py-0.5 text-[11px] font-medium text-primary transition hover:bg-muted"
                >
                  {showAllCategoryFilters ? 'Show less' : `Show all +${hiddenCategoryCount}`}
                </button>
              ) : null}
            </div>
          ) : null}
        </CardHeader>

        <CardContent className="p-0">
          {isLoading && viewMode !== 'calendar' ? (
            viewMode === 'cards' ? (
              <div className="grid gap-2.5 p-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {Array.from({ length: 10 }).map((_, index) => (
                  <JobListCardSkeleton key={index} />
                ))}
              </div>
            ) : (
              <div className="space-y-2 p-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="rounded-lg border border-border/70 p-3">
                    <Skeleton className="h-3.5 w-48" />
                    <Skeleton className="mt-2 h-3.5 w-full" />
                  </div>
                ))}
              </div>
            )
          ) : jobs.length === 0 && viewMode !== 'calendar' ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
              <Briefcase className="h-8 w-8 opacity-60" />
              <div>
                <p className="font-medium text-foreground">{`No ${entityLabelPlural.toLowerCase()} found`}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {`Adjust your filters or create a new ${entityLabel.toLowerCase()} to populate the registry.`}
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="h-8">
                <Link to={withServiceInstanceId(`${entityBasePath}/create`, serviceInstanceId)}>
                  {`Create ${entityLabel}`}
                </Link>
              </Button>
            </div>
          ) : viewMode === 'calendar' ? (
            <JobsCalendarView
              jobs={jobs}
              workDayEntries={calendarWorkDayEntries}
              selectedDate={selectedCalendarDate}
              onSelectDate={(date) => {
                if (date) {
                  setSelectedCalendarDate(date)
                  setVisibleCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1))
                  replaceListQuery({ calendarDate: toJobCalendarDateKey(date) })
                }
              }}
              onVisibleMonthChange={setVisibleCalendarMonth}
              isLoading={calendarViewLoading}
              serviceInstanceId={serviceInstanceId}
              assignmentReturnPath={currentReturnPath}
              relativeTimeNow={relativeTimeNow}
              onOpen={(job) => navigateToJobDetail(String(job.id))}
              onOpenJobById={(jobId) => navigateToJobDetail(String(jobId))}
              onChat={chatEnabled ? openJobChat : undefined}
              onDelete={(job) => setDeleteId(job.id)}
              onOrderSelect={setSelectedOrderReferenceId}
              onStatusApplied={() => {
                queryClient.invalidateQueries({ queryKey: ['jobs'] })
              }}
              lockMutation={lockMutation}
            />
          ) : viewMode === 'cards' ? (
            <>
              <div className="grid gap-2.5 p-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {jobs.map((job: Job) => (
                  <JobListCard
                    key={job.id}
                    job={job}
                    serviceInstanceId={serviceInstanceId}
                    assignmentReturnPath={currentReturnPath}
                    relativeTimeNow={relativeTimeNow}
                    onOpen={() => navigateToJobDetail(String(job.id))}
                    onChat={chatEnabled ? () => openJobChat(job) : undefined}
                    onDelete={() => setDeleteId(job.id)}
                    onOrderSelect={setSelectedOrderReferenceId}
                    onStatusApplied={() => {
                      queryClient.invalidateQueries({ queryKey: ['jobs'] })
                      queryClient.invalidateQueries({ queryKey: ['job', job.id] })
                    }}
                    lockMutation={lockMutation}
                  />
                ))}
              </div>
              {data && data.count > 0 ? (
                <DataTablePagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={data.count}
                  onPageChange={(nextPage) => replaceListQuery({ page: nextPage })}
                  onPageSizeChange={(size) => replaceListQuery({ pageSize: size, page: 1 })}
                />
              ) : null}
            </>
          ) : (
            <>
              <div className={`overflow-x-auto ${commerceTableClassName}`}>
                <Table>
                  <TableHeader><TableRow><TableHead className="pl-4">{entityLabel}</TableHead><TableHead>{labels.entityIdLabel}</TableHead><TableHead>Category</TableHead><TableHead>Incharge</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead>Amount</TableHead><TableHead>Payment</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {jobs.map((job: Job) => {
                      const recentLabel = getRecentJobLabel(job.created_at, relativeTimeNow)
                      const showNewBadge = isNewJob(job.created_at, relativeTimeNow)
                      const recurrenceLabel = getRecurrenceLabel(job)
                      const scheduleState = resolveJobScheduleState(job, relativeTimeNow)
                      const categoryColor = getJobCategoryColor(resolveJobCategoryKey(job))
                      const budgetOverrun = getJobBudgetOverrun(job)
                      const spendSummary = getJobSpendSummary(job)
                      const paymentSummary = formatJobPaymentColumn(job)

                      return (
                        <TableRow
                          key={job.id}
                          className={cn(
                            'cursor-pointer',
                            scheduleState === 'in_progress' && 'bg-amber-50/30',
                            scheduleState === 'overdue' && 'bg-orange-50/25',
                            scheduleState === 'upcoming' && 'bg-sky-50/20',
                          )}
                          onClick={(event) => {
                            const target = event.target as HTMLElement | null
                            if (target?.closest('[data-row-action="true"]')) {
                              return
                            }
                            navigateToJobDetail(String(job.id))
                          }}
                        >
                          <TableCell className="max-w-[220px] whitespace-normal pl-4 font-medium">
                            <div className="space-y-0.5 font-normal">
                              <span className="flex flex-wrap items-center gap-1.5">
                                <span>{formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref, entityLabel: labels.entity })}</span>
                                <Link
                                  to={withReturnTo(
                                    withServiceInstanceId(`/job/locations?job_id=${job.id}`, serviceInstanceId),
                                    currentReturnPath,
                                  )}
                                  data-row-action="true"
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
                                  title="Open worker location assignment map"
                                  aria-label="Open worker location assignment map"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <MapPin className="h-3.5 w-3.5" />
                                </Link>
                                {showNewBadge ? (
                                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                                    New
                                  </span>
                                ) : null}
                                {job.is_locked ? (
                                  <Badge variant="outline" className={JOB_LOCKED_BADGE_CLASS}>
                                    <Lock className="h-3 w-3" />
                                    Locked
                                  </Badge>
                                ) : null}
                                {Number(job.workday_entry_clash_count ?? 0) > 0 ? (
                                  <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800">
                                    <AlertTriangle className="h-3 w-3" />
                                    {Number(job.workday_entry_clash_count ?? 0)} clash{Number(job.workday_entry_clash_count ?? 0) === 1 ? '' : 'es'}
                                  </Badge>
                                ) : null}
                                {recurrenceLabel ? (
                                  <Badge variant="secondary" className="gap-1 px-1.5 py-0 text-[10px] font-medium">
                                    <Repeat className="h-3 w-3" />
                                    {recurrenceLabel}
                                  </Badge>
                                ) : null}
                                {scheduleState === 'overdue' ? (
                                  <JobOverdueBadge className="px-1.5 py-0 text-[10px] font-medium" />
                                ) : scheduleState !== 'none' ? (
                                  <Badge variant="outline" className={cn('px-1.5 py-0 text-[10px] font-medium', jobScheduleStateBadgeClasses[scheduleState])}>
                                    {jobScheduleStateLabels[scheduleState]}
                                  </Badge>
                                ) : null}
                                {recentLabel ? (
                                  <span className="text-[10px] font-medium text-muted-foreground">
                                    {recentLabel}
                                  </span>
                                ) : null}
                              </span>
                              <p className="text-xs text-muted-foreground">
                                {job.location || 'No location provided'}
                                {job.recurrence_parent_info ? (
                                  <span className="text-muted-foreground/80"> · Generated occurrence</span>
                                ) : null}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            <div className="space-y-1">
                              <div className="text-xs">{job.job_ref || '-'}</div>
                              <Badge variant="outline" className={cn('px-1.5 py-0 text-[10px] font-medium', jobPriorityColors[job.priority] || jobPriorityColors.medium)}>
                                {jobPriorityLabels[job.priority] || job.priority}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            {job.category_name ? (
                              <Badge variant="outline" className={cn('text-[10px] font-medium', categoryColor.badge)}>
                                {job.category_name}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {job.primary_worker && job.primary_worker_info?.name ? (
                              <Link to={withServiceInstanceId(`/workers/${job.primary_worker}`, serviceInstanceId)} className="text-primary hover:underline" onClick={(event) => event.stopPropagation()}>
                                {job.primary_worker_info.name}
                              </Link>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={jobStatusColors[job.status] || jobStatusColors.draft}>
                              {job.status.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <CompactTableDate value={job.created_at} />
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="space-y-1">
                              <div>{formatJobAmount(job)}</div>
                              <div className="space-y-0.5 text-[11px] font-normal leading-tight text-muted-foreground">
                                <div>Est spent: {spendSummary.estimated === null ? '-' : formatCurrency(spendSummary.estimated)}</div>
                                <div>Actual spent: {spendSummary.actual === null ? '-' : formatCurrency(spendSummary.actual)}</div>
                              </div>
                              {budgetOverrun ? (
                                <Badge
                                  variant="outline"
                                  className="gap-1 border-rose-300 bg-rose-50 px-1.5 py-0 text-[10px] font-medium text-rose-700"
                                  title={`Actual spent ${formatCurrency(budgetOverrun.expenses)} exceeds estimated spent ${formatCurrency(budgetOverrun.budget)}`}
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  Over by {formatCurrency(budgetOverrun.overBy)}
                                </Badge>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="space-y-1">
                              <div className="text-[11px] font-normal leading-tight text-muted-foreground">
                                Advance
                              </div>
                              <div>{paymentSummary.advanceLabel}</div>
                              {paymentSummary.dueLabel !== null ? (
                                <div className="space-y-0.5 text-[11px] font-normal leading-tight text-muted-foreground">
                                  <div>Due</div>
                                  <div className="font-medium text-foreground">{paymentSummary.dueLabel}</div>
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <CompactTableDate value={job.start_date ?? job.scheduled_start} includeTime includeYear={false} includeWeekday />
                          </TableCell>
                          <TableCell>
                            <CompactTableDate value={job.end_date ?? job.scheduled_end} includeTime includeYear={false} includeWeekday />
                          </TableCell>
                          <TableCell className="text-right" data-row-action="true">
                            <JobListActionsMenu
                              job={job}
                              serviceInstanceId={serviceInstanceId}
                              assignmentReturnPath={currentReturnPath}
                              onView={() => navigateToJobDetail(String(job.id))}
                              onChat={chatEnabled ? () => openJobChat(job) : undefined}
                              onDelete={() => setDeleteId(job.id)}
                              onOrderSelect={setSelectedOrderReferenceId}
                              onStatusApplied={() => {
                                queryClient.invalidateQueries({ queryKey: ['jobs'] })
                                queryClient.invalidateQueries({ queryKey: ['job', job.id] })
                              }}
                              lockMutation={lockMutation}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {data && data.count > 0 && (
                <DataTablePagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={data.count}
                  onPageChange={(nextPage) => replaceListQuery({ page: nextPage })}
                  onPageSizeChange={(size) => replaceListQuery({ pageSize: size, page: 1 })}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!deleteId}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setDeleteId(null)
            setDeleteConfirmInput('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete ${entityLabel}`}</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to delete this ${entityLabel.toLowerCase()}? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Type{' '}
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold text-foreground">
                {deleteConfirmationPhrase}
              </span>{' '}
              to confirm.
            </p>
            <Input
              value={deleteConfirmInput}
              onChange={(event) => setDeleteConfirmInput(event.target.value)}
              placeholder={deleteConfirmationPhrase}
              autoFocus
              disabled={deleteMutation.isPending}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter'
                  && deleteConfirmInput === deleteConfirmationPhrase
                  && deleteId
                  && !deleteMutation.isPending
                ) {
                  event.preventDefault()
                  const reasons = deleteCancellationReasonsQuery.data ?? []
                  const reasonHint = reasons.length > 0
                    ? `Allowed reasons:\n${reasons.map((reason) => `- ${reason.label}`).join('\n')}`
                    : 'Examples: Booking cancelled, Resources unavailable'
                  const reason = window.prompt(`Enter reason.\n${reasonHint}`)
                  if (!reason?.trim()) {
                    toast.error(`Reason is required to delete this ${entityLabel.toLowerCase()}`)
                    return
                  }
                  deleteMutation.mutate({ id: deleteId, reason: reason.trim() })
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                if (deleteId) {
                  const reasons = deleteCancellationReasonsQuery.data ?? []
                  const reasonHint = reasons.length > 0
                    ? `Allowed reasons:\n${reasons.map((reason) => `- ${reason.label}`).join('\n')}`
                    : 'Examples: Booking cancelled, Resources unavailable'
                  const reason = window.prompt(`Enter reason.\n${reasonHint}`)
                  if (!reason?.trim()) {
                    toast.error(`Reason is required to delete this ${entityLabel.toLowerCase()}`)
                    return
                  }
                  deleteMutation.mutate({ id: deleteId, reason: reason.trim() })
                }
              }}
              disabled={deleteConfirmInput !== deleteConfirmationPhrase || !deleteId || deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OrderDetailsDialog
        referenceId={selectedOrderReferenceId}
        open={Boolean(selectedOrderReferenceId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOrderReferenceId(null)
          }
        }}
        viewMode="job-management"
      />

      <JobChatSheet
        job={chatJob}
        open={Boolean(chatJob) && chatEnabled}
        onOpenChange={(open) => {
          if (!open) {
            setChatJob(null)
          }
        }}
      />
    </div>
  )
}
